import { FC, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { LanguageSelection, LineLanguageSelection, Select } from 'yolda-web-ui'
import { RiCustomerService2Line, RiLogoutBoxRLine, RiPhoneFill, RiUserLine } from 'react-icons/ri'

import ImagePaths from '../../../utils/constants/ImagePaths'
import { StyledHeader, Logo, MobileMenuHandler, RightSection, HeaderItemWrapper } from './Header.style'
import { Navigation } from '../../atoms'
import {
  getNavigation,
  MainNavigationType,
  NAVIGATION_KEY,
  SideBarDataType,
} from '../../../utils/constants/Navigation'

import { COUNTRY_DATA_KEY, getCountries } from '../../../utils/constants/Countries'
import urls from '../../../routers/urls'
import AuthService from '../../../services/AuthService'
import { LanguageHelper, UiHelper, UrlHelper } from '../../../utils/helpers'
import Burger from '../../atoms/Burger'
import MobilMenu from '../../atoms/MobilMenu'
import TenantService from '../../../services/TenantService'
import { uiAtom } from '../../../recoil/atoms'

interface HeaderProps {
  mobile?: {
    mainNavigation: MainNavigationType
    subMenus: {
      [x: string]: SideBarDataType
    }
  }
  onLogoClick?: () => void
  pathname?: string
  publicPage?: boolean
}

const COMPANY_PHONE_NUMBER: string = '+90 (850) 640 06 16'

const Header: FC<HeaderProps> = ({ publicPage, mobile, onLogoClick = () => null, pathname }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()
  const indexFromQuery = search ? Number(UrlHelper.getQueryParam(search, 'index')) : undefined
  const [uiState, setUIState] = useRecoilState(uiAtom)
  const { data: user } = TenantService.getCurrentUser(false, false)
  const countries: any = getCountries(COUNTRY_DATA_KEY.LANGUAGE_SELECTION_DATA, t)
  const headerMenuData = getNavigation(
    NAVIGATION_KEY.HEADER_MENU_DATA,
    t,
    user?.data?.tenant?.enabledFeatures,
    user?.data?.role
  )
  const [open, setOpen] = useState<boolean>(false)
  const [languageLoading, setLanguageLoading] = useState<boolean>(false)

  const updateUserLanguage = async (language: string) => {
    try {
      setLanguageLoading(true)
      await TenantService.UpdateCurrentUserFetcher({ language })
    } catch (err) {
      console.error(err)
    } finally {
      setLanguageLoading(false)
    }
  }

  const onLanguageChange = async (val?: string) => {
    if (!val) {
      return
    }

    LanguageHelper.change(val)
    !publicPage && updateUserLanguage(val)
  }

  const onAction = (path: string, index?: number, replace = false) => {
    navigate(`${path}${typeof index === 'number' ? `?index=${index}` : ''}`, { replace })
  }

  const onSignOut = (arg1?: string, arg2?: never) => {
    const currentLang = LanguageHelper.get()
    AuthService.signOut()
    LanguageHelper.change(currentLang)
    window.location.replace(`${window.location.origin}/auth/phone-number`)
  }

  const actions = {
    [urls.Authentication]: onSignOut,
    [urls.CarrierHome]: (path: string, index: number) => onAction(path, index, true),
    [urls.Invoices]: (path: string, _: number) => onAction(path),
    [urls.Loads]: (path: string, _: number) => onAction(path),
  }

  const onNavigationItemClick = (path: string, index?: number) => {
    if (path in actions) {
      setOpen(false)

      typeof index === 'number' &&
        setUIState(prev => ({
          ...prev,
          sidebar: {
            ...prev.sidebar,
            active: index,
          },
        }))

      actions[path](path, (index || 0) as never)
      UiHelper.toggleBodyScroll()
    }
  }

  return (
    <StyledHeader data-cy="header">
      <button onClick={onLogoClick}>
        <Logo src={ImagePaths.branding['logo-black']} />
      </button>
      {mobile && (
        <MobileMenuHandler>
          <Burger
            text="Menu"
            open={open}
            setOpen={(val: boolean) => {
              UiHelper.toggleBodyScroll()
              setOpen(val)
            }}
          />
          <MobilMenu
            activeSubIndex={(indexFromQuery || uiState?.sidebar?.active || 0) as number}
            data={{ ...mobile, mainNavigation: [...mobile.mainNavigation, ...headerMenuData] }}
            handler={onNavigationItemClick}
            open={open}
            pathname={pathname || ''}
            user={user?.data}
          >
            <LineLanguageSelection
              key={user?.data?.language}
              countries={countries || []}
              defaultLanguage={user?.data?.language || LanguageHelper.get()}
              onChange={onLanguageChange}
              isLoading={languageLoading}
            />
          </MobilMenu>
        </MobileMenuHandler>
      )}
      <RightSection publicPage={publicPage}>
        <HeaderItemWrapper>
          <Select
            selected={t('account.support')}
            data={[{ value: COMPANY_PHONE_NUMBER, text: COMPANY_PHONE_NUMBER }]}
            preIcon={<RiCustomerService2Line size={20} />}
            renderOption={data => (
              <div>
                <RiPhoneFill size={20} />
                {data?.text}
              </div>
            )}
          />
        </HeaderItemWrapper>
        {(!publicPage || !!user?.data) && (
          <HeaderItemWrapper>
            <Select
              selected={user?.data?.fullName}
              data={[{ value: 'logout', text: t('account.signOut') }]}
              preIcon={<RiUserLine size={20} />}
              onChange={() => onNavigationItemClick(urls.Authentication)}
              renderOption={data => (
                <div>
                  <RiLogoutBoxRLine size={20} />
                  {data?.text}
                </div>
              )}
            />
          </HeaderItemWrapper>
        )}

        <LanguageSelection
          countries={countries}
          defaultLanguage={user?.data?.language || LanguageHelper.get()}
          onChange={onLanguageChange}
          isLoading={languageLoading}
          variant="padding"
          showCountryCode
        />
      </RightSection>
    </StyledHeader>
  )
}

export default Header
